import { useCallback } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { LinkIntent, linkPathIn, shareUrl } from '@zercode/api-client';
import { useInboxStream } from '@zercode/auth';
import { NOTIFICATION_BELL_QUERY_KEY, NotificationBell, NotificationBellLabels } from '@zercode/forms';

import { loginUrl, notificationClient, NOTIFICATION_URL } from '../lib/zercode';

// ==============================|| HEADER NOTIFICATIONS (NOTIF-DRAWER) ||============================== //
//
// The admin app's copy of the header bell: the shared @zercode/forms NotificationBell, with this app's
// catalogue keys, routes and live updates. It lives OUTSIDE src/berry on purpose — the berry folder is
// excluded from i18n extraction in admin and student, which is why the old bell shipped in English.
//
// ⚠ Every id below is a LITERAL. Category names go through a switch rather than `notif-bell-cat-${c}`,
// which extraction and the coverage gate cannot see (CLAUDE.md, i18n trap 2).
// Admin has both pages, so the drawer links to them; the inbox page's own list is refreshed after a write.

function useBellLabels(): NotificationBellLabels {
  const intl = useIntl();
  const category = (value: string): string => {
    switch (value) {
      case 'security':
        return intl.formatMessage({ id: 'notif-bell-cat-security', defaultMessage: 'Security' });
      case 'tenant_lifecycle':
        return intl.formatMessage({ id: 'notif-bell-cat-tenant-lifecycle', defaultMessage: 'Organisation' });
      case 'billing':
        return intl.formatMessage({ id: 'notif-bell-cat-billing', defaultMessage: 'Billing' });
      case 'system':
        return intl.formatMessage({ id: 'notif-bell-cat-system', defaultMessage: 'System' });
      case 'event_reminder':
        return intl.formatMessage({ id: 'notif-bell-cat-event-reminder', defaultMessage: 'Event reminders' });
      case 'appointment':
        return intl.formatMessage({ id: 'notif-bell-cat-appointment', defaultMessage: 'Appointments' });
      case 'event_invitation':
        return intl.formatMessage({ id: 'notif-bell-cat-event-invitation', defaultMessage: 'Event invitations' });
      case 'offer_decision':
        return intl.formatMessage({ id: 'notif-bell-cat-offer-decision', defaultMessage: 'Offers' });
      case 'role_request':
        return intl.formatMessage({ id: 'notif-bell-cat-role-request', defaultMessage: 'Role requests' });
      case 'payment_decision':
        return intl.formatMessage({ id: 'notif-bell-cat-payment-decision', defaultMessage: 'Payments' });
      case 'event_registration':
        return intl.formatMessage({ id: 'notif-bell-cat-event-registration', defaultMessage: 'Registrations' });
      case 'exam':
        return intl.formatMessage({ id: 'notif-bell-cat-exam', defaultMessage: 'Exams' });
      case 'live_session':
        return intl.formatMessage({ id: 'notif-bell-cat-live-session', defaultMessage: 'Live lessons' });
      case 'task':
        return intl.formatMessage({ id: 'notif-bell-cat-task', defaultMessage: 'Tasks' });
      case 'sales':
        return intl.formatMessage({ id: 'notif-bell-cat-sales', defaultMessage: 'Sales' });
      default:
        return intl.formatMessage({ id: 'notif-bell-cat-other', defaultMessage: 'Other' });
    }
  };

  return {
    bellAria: (count) =>
      intl.formatMessage(
        { id: 'notif-bell-aria', defaultMessage: '{count, plural, =0 {Notifications} other {Notifications, # unread}}' },
        { count }
      ),
    title: intl.formatMessage({ id: 'notif-bell-title', defaultMessage: 'Notifications' }),
    close: intl.formatMessage({ id: 'notif-bell-close', defaultMessage: 'Close' }),
    settings: intl.formatMessage({ id: 'notif-bell-settings', defaultMessage: 'Notification settings' }),
    tabAll: intl.formatMessage({ id: 'notif-bell-tab-all', defaultMessage: 'All' }),
    tabUnread: intl.formatMessage({ id: 'notif-bell-tab-unread', defaultMessage: 'Unread' }),
    tabActions: intl.formatMessage({ id: 'notif-bell-tab-actions', defaultMessage: 'Action needed' }),
    groupToday: intl.formatMessage({ id: 'notif-bell-group-today', defaultMessage: 'Today' }),
    groupYesterday: intl.formatMessage({ id: 'notif-bell-group-yesterday', defaultMessage: 'Yesterday' }),
    groupEarlier: intl.formatMessage({ id: 'notif-bell-group-earlier', defaultMessage: 'Earlier' }),
    moreActions: intl.formatMessage({ id: 'notif-bell-more', defaultMessage: 'More actions' }),
    open: intl.formatMessage({ id: 'notif-bell-open', defaultMessage: 'Open' }),
    markRead: intl.formatMessage({ id: 'notif-bell-mark-read', defaultMessage: 'Mark as read' }),
    markUnread: intl.formatMessage({ id: 'notif-bell-mark-unread', defaultMessage: 'Mark as unread' }),
    markAllRead: intl.formatMessage({ id: 'notif-bell-mark-all-read', defaultMessage: 'Mark all as read' }),
    markDone: intl.formatMessage({ id: 'notif-bell-mark-done', defaultMessage: 'Mark done' }),
    reopen: intl.formatMessage({ id: 'notif-bell-reopen', defaultMessage: 'Reopen' }),
    actionNeeded: intl.formatMessage({ id: 'notif-bell-action-needed', defaultMessage: 'Action needed' }),
    actionDone: intl.formatMessage({ id: 'notif-bell-action-done', defaultMessage: 'Done' }),
    muteCategory: (name) =>
      intl.formatMessage({ id: 'notif-bell-mute', defaultMessage: 'Turn off {category} notifications' }, { category: name }),
    unmuteCategory: (name) =>
      intl.formatMessage({ id: 'notif-bell-unmute', defaultMessage: 'Turn on {category} notifications' }, { category: name }),
    mutedNotice: (name) =>
      intl.formatMessage(
        { id: 'notif-bell-muted', defaultMessage: "You won't get new {category} notifications here." },
        { category: name }
      ),
    unmutedNotice: (name) =>
      intl.formatMessage({ id: 'notif-bell-unmuted', defaultMessage: '{category} notifications are on again.' }, { category: name }),
    undo: intl.formatMessage({ id: 'notif-bell-undo', defaultMessage: 'Undo' }),
    emptyAllTitle: intl.formatMessage({ id: 'notif-bell-empty-all-title', defaultMessage: 'No notifications yet' }),
    emptyAllBody: intl.formatMessage({
      id: 'notif-bell-empty-all-body',
      defaultMessage: 'Updates about your courses, exams and orders will show up here.'
    }),
    emptyUnreadTitle: intl.formatMessage({ id: 'notif-bell-empty-unread-title', defaultMessage: "You're all caught up" }),
    emptyUnreadBody: intl.formatMessage({
      id: 'notif-bell-empty-unread-body',
      defaultMessage: 'Nothing unread. Older notifications are under All.'
    }),
    emptyActionsTitle: intl.formatMessage({ id: 'notif-bell-empty-actions-title', defaultMessage: 'Nothing is waiting on you' }),
    emptyActionsBody: intl.formatMessage({
      id: 'notif-bell-empty-actions-body',
      defaultMessage: 'Requests to review, refunds and other to-dos appear here until you mark them done.'
    }),
    loadMore: intl.formatMessage({ id: 'notif-bell-load-more', defaultMessage: 'Load more' }),
    loadError: intl.formatMessage({ id: 'notif-bell-load-error', defaultMessage: 'Notifications could not be loaded.' }),
    retry: intl.formatMessage({ id: 'notif-bell-retry', defaultMessage: 'Retry' }),
    fullInbox: intl.formatMessage({ id: 'notif-bell-full-inbox', defaultMessage: 'Full inbox' }),
    category
  };
}

export default function HeaderNotifications() {
  const intl = useIntl();
  const labels = useBellLabels();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // live inbox updates over SSE; the bell's own 2-minute poll is only the safety net
  const onInboxChanged = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: NOTIFICATION_BELL_QUERY_KEY });
    void queryClient.invalidateQueries({ queryKey: ['notif-inbox'] });
  }, [queryClient]);
  useInboxStream(NOTIFICATION_URL, onInboxChanged);

  // NOTIF-DRAWER phase 2: open what a message is about. Inside this app when the kind has a page here (no round
  // trip); otherwise through the share link, which the login origin resolves against the reader's own apps —
  // the same address a pasted link would use, so "not available for your account" is answered there, honestly.
  const openLink = (intent: LinkIntent) => {
    const inApp = linkPathIn('admin', intent);
    if (inApp) navigate(inApp);
    else window.location.assign(shareUrl(loginUrl, intent));
  };

  return (
    <NotificationBell
      client={notificationClient}
      labels={labels}
      locale={intl.locale}
      openLink={openLink}
      onOpenInbox={() => navigate('/notifications/inbox')}
      onOpenPreferences={() => navigate('/notifications/preferences')}
      onInboxChanged={onInboxChanged}
    />
  );
}
